import { Component, OnInit, Output, EventEmitter, Input } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { InventoryItem, InventoryService, MachineOption } from "../../../inventory";
import { ToastrService } from "ngx-toastr";
import { OpenAIService, OptionSuggestion } from "../../../../rubbl-shared/services/open-ai.service";
import { LocalStorageService } from "../../../../core/core";
import { CheckboxCard } from "../../../../forms/forms";

@Component({
  selector: "common-machine-create-wizard-options",
  templateUrl: "./machine-create-wizard-options.component.html",
  styleUrls: ["./machine-create-wizard-options.component.scss"],
})
export class MachineCreateWizardOptionsComponent implements OnInit {
  @Input() machineId: string;
  @Output() optionsSaved = new EventEmitter<MachineOption[]>();

  // Machine data
  machine: InventoryItem | null = null;
  machineLoading: boolean = false;

  // OpenAI suggestions
  optionSuggestions: OptionSuggestion[] = [];
  optionCheckboxCards: CheckboxCard[] = [];
  suggestionsLoading: boolean = false;
  selectedOptions: { [key: string]: boolean } = {};

  // Form data
  loading: boolean = false;
  validationErrors: string[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private inventoryService: InventoryService,
    private openAIService: OpenAIService,
    private localStorageService: LocalStorageService,
  ) {}

  ngOnInit(): void {
    // Load machine then get suggestions
    this.loadMachineFromParams();
  }

  private loadMachineFromParams(): void {
    const machineId = this.route.snapshot.params["id"];
    const partitionKey = this.route.snapshot.queryParams["partitionKey"];

    if (machineId && partitionKey) {
      this.loadMachine(machineId, partitionKey);
    } else {
      this.toastrService.error("Machine ID or partition key not found. Please try creating the machine again.");
      this.router.navigate(["/owner/fleet"]);
    }
  }

  private loadMachine(machineId: string, partitionKey: string): void {
    this.machineLoading = true;

    this.inventoryService.getWithPartitionKey(machineId, partitionKey).subscribe({
      next: (machine: InventoryItem) => {
        this.machine = machine;
        this.machineId = machineId;
        this.machineLoading = false;

        // Load existing options from the machine FIRST
        const existingOptions = machine.options || [];

        // Check for cached suggestions before calling AI
        const cachedSuggestions = this.getCachedSuggestions(machineId);
        if (cachedSuggestions) {
          this.buildCheckboxCardsFromSuggestions(cachedSuggestions, existingOptions);
        } else {
          this.getOptionSuggestions(machine, existingOptions);
        }
      },
      error: () => {
        this.toastrService.error("Failed to load machine details. Please try again.");
        this.machineLoading = false;
        this.router.navigate(["/owner/fleet"]);
      },
    });
  }

  private getCachedSuggestions(machineId: string): OptionSuggestion[] | null {
    const cached = this.localStorageService.get(`options-${machineId}`, true) as OptionSuggestion[] | null;
    return cached && Array.isArray(cached) ? cached : null;
  }

  private cacheSuggestions(machineId: string, suggestions: OptionSuggestion[]): void {
    this.localStorageService.set(`options-${machineId}`, suggestions, true);
  }

  private buildCheckboxCardsFromSuggestions(suggestions: OptionSuggestion[], existingOptions: MachineOption[]): void {
    const sortedSuggestions = [...suggestions].sort((a, b) =>
      (a?.name || "").localeCompare((b?.name || "")),
    );

    this.optionSuggestions = sortedSuggestions;
    const existingOptionNames = new Set(existingOptions.map(opt => opt.name));

    this.optionCheckboxCards = sortedSuggestions.map(suggestion => ({
      value: suggestion.name,
      label: suggestion.name,
      checked: existingOptionNames.has(suggestion.name),
      body: suggestion.description,
    }));

    sortedSuggestions.forEach(suggestion => {
      this.selectedOptions[suggestion.name] = existingOptionNames.has(suggestion.name);
    });
  }

  private getOptionSuggestions(machine: InventoryItem, existingOptions: MachineOption[]): void {
    if (!machine.make || !machine.model || !machine.year) {
      this.toastrService.warning("Machine details incomplete. Skipping option suggestions.");
      return;
    }

    // Get machine type directly from API response
    const machineType = machine.typeDefinitionName || machine.primaryType || machine.primaryCategory || (machine.categories && machine.categories[0]);

    if (!machineType) {
      this.toastrService.warning("Machine type missing. Skipping option suggestions.");
      return;
    }

    this.suggestionsLoading = true;

    this.openAIService.getOptionSuggestions(machine.make, machine.model, machine.year.toString(), machineType).subscribe({
      next: (result: OptionSuggestion[] | null) => {
        this.suggestionsLoading = false;

        if (result && result.length > 0) {
          // Cache the full suggestion list for this machine
          this.cacheSuggestions(this.machineId, result);

          this.buildCheckboxCardsFromSuggestions(result, existingOptions);
        }
      },
      error: () => {
        this.suggestionsLoading = false;
        this.toastrService.warning("Unable to get option suggestions. You can still continue.");
      },
    });
  }

  onSingleOptionCheckboxChange(cardValue: string, checkboxCards: CheckboxCard[]): void {
    // Update the specific card in our main array
    const updatedCard = checkboxCards[0]; // Since we're passing a single card array
    const cardIndex = this.optionCheckboxCards.findIndex(card => card.value === cardValue);
    if (cardIndex !== -1) {
      this.optionCheckboxCards[cardIndex] = updatedCard;
      this.selectedOptions[cardValue] = updatedCard.checked;
    }
  }

  hasSelectedOptions(): boolean {
    return Object.values(this.selectedOptions).some(selected => selected);
  }

  getSelectedOptionsArray(): string[] {
    return Object.keys(this.selectedOptions).filter(key => this.selectedOptions[key]);
  }

  // Validation methods
  canProceed(): boolean {
    this.validationErrors = [];
    // Options are optional, so we can always proceed
    return true;
  }

  // Navigation methods
  onBack(): void {
    const currentUrl = this.router.url;
    const baseUrl = currentUrl.split("/").slice(0, -1).join("/");
    this.router.navigate([baseUrl, "pricing"], {
      queryParams: { partitionKey: this.route.snapshot.queryParams["partitionKey"] },
    });
  }

  onNext(): void {
    if (this.loading || !this.canProceed()) {
      return;
    }

    this.loading = true;

    // Convert selected options to MachineOption objects
    const machineOptions: MachineOption[] = this.getSelectedOptionsArray().map(optionName => ({
      name: optionName,
    }));

    // Description is generated on the attachments step so attachment names are accurate (options runs first).
    const patchOperations = [
      {
        op: "replace",
        path: "/options",
        value: machineOptions,
      },
    ];

    this.inventoryService.jsonPatchWithPartitionKey(this.machineId, patchOperations, this.route.snapshot.queryParams["partitionKey"]).subscribe({
      next: () => {
        this.optionsSaved.emit(machineOptions);
        this.navigateToAttachments();
      },
      error: () => {
        this.toastrService.error("Failed to save options. Please try again.");
        this.loading = false;
      },
    });
  }

  private navigateToAttachments(): void {
    const currentUrl = this.router.url;
    const baseUrl = currentUrl.split("/").slice(0, -1).join("/");
    this.router.navigate([baseUrl, "attachments"], {
      queryParams: { partitionKey: this.route.snapshot.queryParams["partitionKey"] },
    });

    this.loading = false;
  }
}
